import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderInput, Trash2, X } from "lucide-react";

import { useActiveChat, useChatMessages } from "~/entities/chat/useActiveChat";
import { Button } from "~/shared/ui/button";

import { DeleteMessagesDialog } from "./DeleteMessagesDialog";
import { MoveMessagesDialog } from "./MoveMessagesDialog";
import { useMessageSelection } from "./selection-context";

// Панель массовых действий над выбранными импортированными сообщениями.
// Показывается только когда что-то отмечено. Рендерится под
// MessageSelectionProvider (т.е. только суперадмину — см. ChatShell).
export function MoveSelectionBar({ chatId }: { chatId: string | undefined }) {
    const { t } = useTranslation("chat");
    const selection = useMessageSelection();
    const { data: chat } = useActiveChat(chatId);
    const { data: messages } = useChatMessages(chatId);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    if (!selection || selection.count === 0 || !chatId || !chat) return null;

    // Все сообщения источника выбраны → после переноса/удаления он опустеет.
    const total = messages?.total ?? 0;
    const willEmptySource = total > 0 && selection.count >= total;

    return (
        <div className="flex items-center gap-2 border-b border-white/5 bg-zinc-900/80 px-4 py-2 backdrop-blur-sm md:px-8">
            <span className="text-sm font-medium text-zinc-200 tabular-nums">
                {t("moveMessages.selected", { count: selection.count })}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
                <Button
                    type="button"
                    variant="accent"
                    className="gap-1.5"
                    onClick={() => setDialogOpen(true)}
                >
                    <FolderInput className="size-4" />
                    <span className="hidden sm:inline">{t("moveMessages.moveButton")}</span>
                </Button>
                <Button
                    type="button"
                    variant="destructive"
                    className="gap-1.5"
                    onClick={() => setDeleteDialogOpen(true)}
                >
                    <Trash2 className="size-4" />
                    <span className="hidden sm:inline">{t("deleteMessages.deleteButton")}</span>
                </Button>
                <Button type="button" variant="ghost" className="gap-1.5" onClick={selection.clear}>
                    <X className="size-4" />
                    <span className="hidden sm:inline">{t("moveMessages.cancel")}</span>
                </Button>
            </div>

            <MoveMessagesDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                sourceChatId={chatId}
                workspaceId={chat.workspaceId}
                messageIds={selection.selectedIds}
                willEmptySource={willEmptySource}
                onMoved={selection.clear}
            />

            <DeleteMessagesDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                chatId={chatId}
                workspaceId={chat.workspaceId}
                messageIds={selection.selectedIds}
                willEmptyChat={willEmptySource}
                onDeleted={selection.clear}
            />
        </div>
    );
}
